import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../layout/Header';
import Table, { TableRow, TableCell } from '../common/Table';
import Button from '../common/Button';
import TextField from '../common/TextField';
import LoadingSpinner from '../common/LoadingSpinner';
import StatCard from '../common/StatCard';
import Modal from '../common/Modal';
import Toast, { useToast } from '../common/Toast';
import UniformAPI from '../../firebase/apis/api-uniforms';
import { useAuth } from '../../context/AuthContext';

function UniformManagement() {
  const { user, isAuthenticated, canManageUsers } = useAuth();
  const location = useLocation();
  const { toasts, success, error, warning, removeToast } = useToast();
  const [uniforms, setUniforms] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    team: '',
    status: '',
    paymentStatus: '',
    uniformTop: '',
    uniformBottom: ''
  });
  const [isExporting, setIsExporting] = useState(false);
  const [selectedUniforms, setSelectedUniforms] = useState(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUniform, setEditingUniform] = useState(null);
  const [newUniform, setNewUniform] = useState({
    studentName: '',
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    team: '',
    uniformTop: '',
    uniformBottom: '',
    paymentStatus: 'pending',
    paymentAmount: 0,
    received: false
  });
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [showBulkReceivedConfirmation, setShowBulkReceivedConfirmation] = useState(null);
  const [showBulkNotReceivedConfirmation, setShowBulkNotReceivedConfirmation] = useState(null);
  const [viewingUniform, setViewingUniform] = useState(null);
  const [notifyingOrderId, setNotifyingOrderId] = useState(null);
const [showNotifyConfirm, setShowNotifyConfirm] = useState(false);
const [notifyPayload, setNotifyPayload] = useState({ id: null, email: '', name: '' });
  const [showPrintView, setShowPrintView] = useState(false);
  const [printChecked, setPrintChecked] = useState(new Set());

  const fetchUniformsData = async () => {
    try {
      setLoading(true);

      
      // If there's a search query, use search endpoint, otherwise use getAll with filters (excluding status/paymentStatus to avoid Firestore index issues)
      let uniformsData;
      // Create a copy of filters without status and paymentStatus for backend request
      const backendFilters = { ...filters };
      delete backendFilters.status;
      delete backendFilters.paymentStatus;
      if (searchQuery && searchQuery.trim()) {
        uniformsData = { uniforms: await UniformAPI.searchUniforms(searchQuery, backendFilters) };
      } else {
        uniformsData = await UniformAPI.getAllUniforms(backendFilters);
      }
      
      const summaryData = await UniformAPI.getUniformsSummary();
      console.log("summaryData", summaryData)
      setUniforms(uniformsData.uniforms || []);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error fetching uniforms:', error);
      // Set empty state on error
      setUniforms([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if we have a filter from navigation state
    if (location.state?.filter === 'not_received') {
      setFilters(prev => ({
        ...prev,
        status: 'not_received'
      }));
    }
  }, [location.state]);

  useEffect(() => {
    fetchUniformsData();
  }, [filters]);

  const handleSearch = async () => {
    await fetchUniformsData();
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleReceivedToggle = async (uniformId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await UniformAPI.updateReceivedStatus(uniformId, newStatus, user);
      await fetchUniformsData();
      
      // Trigger immediate update for dashboard card
      window.dispatchEvent(new CustomEvent('uniformStatusChanged', {
        detail: { uniformId, newStatus }
      }));
      
      console.log(`✅ Uniform ${uniformId} marked as ${newStatus ? 'Distributed' : 'Pending'}`);
    } catch (error) {
      console.error('Error updating received status:', error);
    }
  };

  const handleSelectUniform = (uniformId, checked) => {
    const newSelected = new Set(selectedUniforms);
    if (checked) {
      newSelected.add(uniformId);
    } else {
      newSelected.delete(uniformId);
    }
    setSelectedUniforms(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = new Set(filteredUniforms.map(u => u.id));
      setSelectedUniforms(allIds);
      setShowBulkActions(true);
    } else {
      setSelectedUniforms(new Set());
      setShowBulkActions(false);
    }
  };

  // Filter uniforms client‑side based on top/bottom size dropdowns and status filters
  const filteredUniforms = useMemo(() => {
    return uniforms.filter(u => {
      let matches = true;
      // Size filters
      if (filters.uniformTop) {
        const topMatch = u.uniformTop === filters.uniformTop ||
          (u.sizes && u.sizes.some(s => s.item.toLowerCase().includes('top') && s.size === filters.uniformTop));
        matches = matches && topMatch;
      }
      if (filters.uniformBottom) {
        const bottomMatch = u.uniformBottom === filters.uniformBottom ||
          (u.sizes && u.sizes.some(s => s.item.toLowerCase().includes('bottom') && s.size === filters.uniformBottom));
        matches = matches && bottomMatch;
      }
      // Status filter (received/not_received)
      if (filters.status) {
        if (filters.status === 'received') {
          matches = matches && u.received === true;
        } else if (filters.status === 'not_received') {
          matches = matches && u.received === false;
        }
      }
      // Payment status filter (pending/completed)
      if (filters.paymentStatus) {
        const status = filters.paymentStatus.toLowerCase();
        if (status === 'pending' || status === 'pending_payment') {
          matches = matches && u.paymentStatus && u.paymentStatus.toLowerCase() === 'pending';
        } else if (status === 'completed') {
          matches = matches && u.paymentStatus && u.paymentStatus.toLowerCase() === 'completed';
        }
      }
      return matches;
    });
  }, [uniforms, filters]);

  const handleBulkReceivedUpdate = async (received) => {
    try {
      setIsBulkUpdating(true);
      const uniformIds = Array.from(selectedUniforms);
      const updatePromises = uniformIds.map(uniformId =>
        UniformAPI.updateReceivedStatus(uniformId, received, user)
      );
      await Promise.all(updatePromises);
      setSelectedUniforms(new Set());
      setShowBulkActions(false);
      setShowBulkReceivedConfirmation(null);
      setShowBulkNotReceivedConfirmation(null);
      await fetchUniformsData();
      
      // Trigger immediate update for dashboard card
      window.dispatchEvent(new CustomEvent('uniformStatusChanged', {
        detail: { uniformIds, newStatus: received }
      }));
      
      success(`Successfully marked ${uniformIds.length} order${uniformIds.length > 1 ? 's' : ''} as ${received ? 'Received' : 'Not Received'}`);
    } catch (err) {
      console.error('Error updating bulk received status:', err);
      error('Failed to update some orders. Please try again.');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setIsDeleting(true);
      const orderIds = Array.from(selectedUniforms);
      await UniformAPI.batchDeleteOrders(orderIds);
      
      // Clear selection and refresh data
      setSelectedUniforms(new Set());
      setShowBulkActions(false);
      setShowDeleteConfirmation(false);
      await fetchUniformsData();
      
      // Show success message
      success(`Successfully deleted ${orderIds.length} uniform order${orderIds.length > 1 ? 's' : ''}`);
      console.log(`✅ Successfully deleted ${orderIds.length} uniform orders`);
    } catch (error) {
      console.error('Error deleting uniform orders:', error);
      // Show error message
      error(`Error deleting orders: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      const timestamp = new Date().toISOString().split('T')[0];
      await UniformAPI.downloadCSV(filters, `uniforms-export-${timestamp}`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSingleDelete = async (uniformId, uniformName) => {
    if (window.confirm(`Are you sure you want to delete the uniform order for "${uniformName}"? This action cannot be undone.`)) {
      try {
        await UniformAPI.batchDeleteOrders([uniformId]);
        await fetchUniformsData();
        success('Uniform order deleted successfully');
        console.log('✅ Uniform order deleted successfully');
      } catch (error) {
        console.error('Error deleting uniform order:', error);
        error(`Error deleting order: ${error.message}`);
      }
    }
  };

  const handleNotifyParent = async (orderId, parentEmail, studentName) => {
    if (!parentEmail) {
      warning('No parent email on file for this student');
      return;
    }
    // Open custom modal for confirmation
    setNotifyPayload({ id: orderId, email: parentEmail, name: studentName });
    setShowNotifyConfirm(true);
    // The actual notification will be sent after user confirms in the modal.
    return;
  };

  const handleEditSubmit = async () => {
  if (!editingUniform) return;

  try {
    const originalUniform = uniforms.find(u => u.id === editingUniform.id);
    const updateData = {};
    let statusChanged = false;

    // Only send changed fields
    if (editingUniform.studentName !== originalUniform?.studentName) {
      updateData.studentName = editingUniform.studentName;
    }
    if (editingUniform.parentName !== originalUniform?.parentName) {
      updateData.parentName = editingUniform.parentName;
    }
    if (editingUniform.parentEmail !== originalUniform?.parentEmail) {
      updateData.parentEmail = editingUniform.parentEmail;
    }
    if (editingUniform.parentPhone !== originalUniform?.parentPhone) {
      updateData.parentPhone = editingUniform.parentPhone;
    }
    if (editingUniform.team !== originalUniform?.team) {
      updateData.team = editingUniform.team;
    }
    if (editingUniform.uniformTop !== originalUniform?.uniformTop) {
      updateData.uniformTop = editingUniform.uniformTop;
    }
    if (editingUniform.uniformBottom !== originalUniform?.uniformBottom) {
      updateData.uniformBottom = editingUniform.uniformBottom;
    }
    if (editingUniform.paymentStatus !== originalUniform?.paymentStatus) {
      updateData.paymentStatus = editingUniform.paymentStatus;
    }
    if (editingUniform.paymentAmount !== originalUniform?.paymentAmount) {
      updateData.paymentAmount = editingUniform.paymentAmount;
    }
    if (editingUniform.received !== originalUniform?.received) {
      updateData.received = editingUniform.received;
      statusChanged = true;
    }

    // Only make API call if there are changes
    if (Object.keys(updateData).length > 0) {
      await UniformAPI.updateUniform(editingUniform.id, updateData, user);
      await fetchUniformsData();

      // Trigger immediate update for dashboard card if status changed
      if (statusChanged) {
        window.dispatchEvent(new CustomEvent('uniformStatusChanged', {
          detail: { uniformId: editingUniform.id, newStatus: editingUniform.received }
        }));
      }

      setEditingUniform(null);
      success('Uniform order updated successfully!');
    } else {
      setEditingUniform(null);
    }
  } catch (error) {
    console.error('Error updating uniform order:', error);
    error('Failed to update uniform order. Please try again.');
  }
};

  // Confirm notification modal handler
  const handleConfirmNotify = async () => {
    const { id, email, name } = notifyPayload;
    if (!id) return;
    setNotifyingOrderId(id);
    try {
      await UniformAPI.notifyParent(id);
      success(`Notification sent to ${email}`);
    } catch (err) {
      console.error('Error sending notification:', err);
      error(`Failed to send notification: ${err.message}`);
    } finally {
      setNotifyingOrderId(null);
      setShowNotifyConfirm(false);
      setNotifyPayload({ id: null, email: '', name: '' });
    }
  };

  const handleCreateSubmit = async () => {
    try {
      // Validate required fields
      if (!newUniform.studentName.trim()) {
        warning('Please enter student name');
        return;
      }
      if (!newUniform.parentName.trim()) {
        warning('Please enter parent name');
        return;
      }
      if (!newUniform.team.trim()) {
        warning('Please enter team name');
        return;
      }
      
      const createdUniform = await UniformAPI.createUniform(newUniform, user);
      await fetchUniformsData();
      
      // Trigger immediate update for dashboard card
      window.dispatchEvent(new CustomEvent('uniformStatusChanged', {
        detail: { 
          uniformId: createdUniform.data?.id || 'new', 
          newStatus: newUniform.received,
          action: 'created'
        }
      }));
      
      // Reset form and close modal
      setNewUniform({
        studentName: '',
        parentName: '',
        parentEmail: '',
        parentPhone: '',
        team: '',
        uniformTop: '',
        uniformBottom: '',
        paymentStatus: 'pending',
        paymentAmount: 0,
        received: false
      });
      setShowCreateModal(false);
      
      success('Uniform order created successfully!');
      
    } catch (error) {
      console.error('Error creating uniform order:', error);
      error('Failed to create uniform order. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPaymentStatusBadge = (status) => {
    const statusColors = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="text-gray-500 mb-2">Authentication required</div>
          <div className="text-sm text-gray-400">Please log in to access uniform management</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header
        title="Uniform Management"
        subtitle="Track and manage uniform orders and deliveries"
      />

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Orders"
          value={summary.totalOrders || 0}
          icon="📦"
        />
        <StatCard
          title="Not Received"
          value={summary.notReceived || 0}
          icon="⏳"
          valueColor="text-yellow-600"
        />
        <StatCard
          title="Received"
          value={summary.received || 0}
          icon="✅"
          valueColor="text-green-600"
        />
        <StatCard
          title="Pending Payment"
          value={summary.pendingPayment || 0}
          icon="💳"
          valueColor="text-red-600"
        />
      </div>

      {/* Pending Distribution by Size */}
      {(() => {
        const pendingTopSizes = {};
        const pendingBottomSizes = {};
        uniforms.forEach((u) => {
          if (!u.received) {
            const top = u.uniformTop || "";
            const bottom = u.uniformBottom || "";
            if (top) pendingTopSizes[top] = (pendingTopSizes[top] || 0) + 1;
            if (bottom)
              pendingBottomSizes[bottom] =
                (pendingBottomSizes[bottom] || 0) + 1;
            if (u.sizes && Array.isArray(u.sizes)) {
              u.sizes.forEach((s) => {
                if (s.item === "Top" && s.size)
                  pendingTopSizes[s.size] = (pendingTopSizes[s.size] || 0) + 1;
                if (s.item === "Bottom" && s.size)
                  pendingBottomSizes[s.size] =
                    (pendingBottomSizes[s.size] || 0) + 1;
              });
            }
          }
        });
        const hasData =
          Object.keys(pendingTopSizes).length > 0 ||
          Object.keys(pendingBottomSizes).length > 0;
        if (!hasData) return null;
        const notReceived = summary.notReceived || 1;
        return (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-[18px] font-semibold text-gray-700 uppercase tracking-wide mb-4">
              Pending Distribution by Size
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-xs font-medium text-gray-900 uppercase mb-3">
                  Top Sizes
                </h4>
                <div className="space-y-2">
                  {Object.entries(pendingTopSizes)
                    .sort((a, b) => b[1] - a[1])
                    .map(([size, count]) => (
                      <div
                        key={size}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm text-gray-700">{size}</span>
                        <div className="flex items-center space-x-3">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full"
                              style={{
                                width: `${(count / notReceived) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900 w-8 text-right">
                            {count}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-medium text-gray-900 uppercase mb-3">
                  Bottom Sizes
                </h4>
                <div className="space-y-2">
                  {Object.entries(pendingBottomSizes)
                    .sort((a, b) => b[1] - a[1])
                    .map(([size, count]) => (
                      <div
                        key={size}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm text-gray-700">{size}</span>
                        <div className="flex items-center space-x-3">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-orange-400 h-2 rounded-full"
                              style={{
                                width: `${(count / notReceived) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900 w-8 text-right">
                            {count}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}


             {/* Main Filter Controls */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          {/* Search */}
          <div className="md:col-span-5">
            <TextField
              label="Search"
              placeholder="Search by student, parent name, or team..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>

          {/* Team */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
            <select
              value={filters.team}
              onChange={(e) => handleFilterChange("team", e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Teams</option>
              <option value="U8">U8</option>
              <option value="U10">U10</option>
              <option value="U12">U12</option>
              <option value="U14">U14</option>
              <option value="U16">U16</option>
            </select>
          </div>

          {/* Top Size */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Top Size</label>
            <select
              value={filters.uniformTop}
              onChange={(e) => handleFilterChange("uniformTop", e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Top Sizes</option>
              {["Youth XS","Youth S","Youth M","Youth L","Youth XL","Adult S","Adult M","Adult L","Adult XL","Adult XXL"].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Bottom Size */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Bottom Size</label>
            <select
              value={filters.uniformBottom}
              onChange={(e) => handleFilterChange("uniformBottom", e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Bottom Sizes</option>
              {["Youth XS","Youth S","Youth M","Youth L","Youth XL","Adult S","Adult M","Adult L","Adult XL","Adult XXL"].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Search Button */}
          <div className="md:col-span-1">
            <Button onClick={handleSearch} className="w-full py-2.5">
              Search
            </Button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-between items-center gap-3 mt-6 pt-5 border-t border-gray-300">
          <div className="flex gap-3">
            <Button
              onClick={() => {
                setSearchQuery("");
                setFilters({ team: "", status: "", paymentStatus: "", uniformTop: "", uniformBottom: "" });
                setSelectedUniforms(new Set());
                setShowBulkActions(false);
                fetchUniformsData();
              }}
              variant="secondary"
            >
              Clear All Filters
            </Button>

            {canManageUsers() && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2"
              >
                ➕ Add Manual Order
              </Button>
            )}
          </div>

                      {/* Bulk Actions Toolbar */}
            <div className="flex space-x-2">
              {showBulkActions && (
                <>
                  <Button onClick={() => setShowBulkReceivedConfirmation(true)} variant="secondary" className="flex items-center space-x-1">
                    <span>✅</span>
                    <span>Mark Received ({selectedUniforms.size})</span>
                  </Button>
                  <Button onClick={() => setShowBulkNotReceivedConfirmation(true)} variant="secondary" className="flex items-center space-x-1">
                    <span>⏳</span>
                    <span>Mark Not Received ({selectedUniforms.size})</span>
                  </Button>
                  <Button onClick={() => setShowDeleteConfirmation(true)} variant="danger" className="flex items-center space-x-1">
                    <span>🗑️</span>
                    <span>Delete ({selectedUniforms.size})</span>
                  </Button>
                </>
              )}
            </div>
            <div className="flex gap-3">
            <Button onClick={() => setShowPrintView(true)} className="flex items-center gap-2">
              🖨️ Print List
            </Button>
            <Button
              onClick={handleExportCSV}
              disabled={isExporting}
              className="flex items-center gap-2"
            >
              {isExporting ? (
                <><LoadingSpinner size="sm"/> Exporting...</>
              ) : (
                <>📥 Export CSV</>
              )}
            </Button>
          </div>
        </div>

        {/* Quick Filters - Placed at Bottom */}
        <div className="mt-5 pt-3 border-t border-gray-300">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-3">Quick Filters</p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleFilterChange('status', '')}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all ${
                filters.status === '' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filters.status === '' && <span>✓</span>} All
            </button>

            <button
              onClick={() => handleFilterChange('status', 'not_received')}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all ${
                filters.status === 'not_received' ? 'bg-yellow-500 text-white' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
              }`}
            >
              {filters.status === 'not_received' && <span>✓</span>} Not Received
            </button>

            <button
              onClick={() => handleFilterChange('status', 'received')}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all ${
                filters.status === 'received' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {filters.status === 'received' && <span>✓</span>} Received
            </button>

            <button
              onClick={() => handleFilterChange('paymentStatus', 'pending')}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all ${
                filters.paymentStatus === 'pending' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
            >
              {filters.paymentStatus === 'pending' && <span>✓</span>} Pending Payment
            </button>
          </div>
        </div>
      </div>

      {/* Uniforms Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-medium text-gray-900">
              Uniform Orders ({filteredUniforms.length})
            </h3>
            {filters.team && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Team: {filters.team}
                <button
                  type="button"
                  onClick={() => handleFilterChange("team", "")}
                  className="ml-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  &times;
                </button>
              </span>
            )}
            {filters.status && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Status:{" "}
                {filters.status === "not_received"
                  ? "Not Received"
                  : filters.status === "pending_payment"
                    ? "Pending Payment"
                    : "Received"}
                <button
                  type="button"
                  onClick={() => handleFilterChange("status", "")}
                  className="ml-2 text-sm text-purple-600 hover:text-purple-800"
                >
                  &times;
                </button>
              </span>
            )}
            {filters.paymentStatus && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                Payment:{" "}
                {filters.paymentStatus === "pending"
                  ? "Pending Payment"
                  : "Completed"}
                <button
                  type="button"
                  onClick={() => handleFilterChange("paymentStatus", "")}
                  className="ml-2 text-sm text-indigo-600 hover:text-indigo-800"
                >
                  &times;
                </button>
              </span>
            )}
            {filters.uniformTop && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Top: {filters.uniformTop}
                <button
                  type="button"
                  onClick={() => handleFilterChange("uniformTop", "")}
                  className="ml-2 text-sm text-green-600 hover:text-green-800"
                >
                  &times;
                </button>
              </span>
            )}
            {filters.uniformBottom && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                Bottom: {filters.uniformBottom}
                <button
                  type="button"
                  onClick={() => handleFilterChange("uniformBottom", "")}
                  className="ml-2 text-sm text-orange-600 hover:text-orange-800"
                >
                  &times;
                </button>
              </span>
            )}
            {searchQuery && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Search: \"{searchQuery}\"
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="ml-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  &times;
                </button>
              </span>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table
            headers={[
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={
                    selectedUniforms.size === filteredUniforms.length &&
                    filteredUniforms.length > 0
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span></span>
                <span>Actions</span>
              </div>,
              "Student",
              "Parent",
              "Team",
              "Size",
              "Order Date",
              "Payment",
              "Status",
              "Admin Actions",
            ]}
          >
            {uniforms.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-8 text-gray-500"
                >
                  No uniforms found matching your criteria
                </TableCell>
              </TableRow>
            ) : (
              filteredUniforms.map((uniform) => (
                <TableRow key={uniform.id}>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedUniforms.has(uniform.id)}
                        onChange={(e) =>
                          handleSelectUniform(uniform.id, e.target.checked)
                        }
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={uniform.received || false}
                          onChange={() =>
                            handleReceivedToggle(uniform.id, uniform.received)
                          }
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-600">
                          {uniform.received ? "Received" : "Not Received"}
                        </span>
                      </label>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="font-medium text-gray-900">
                      {uniform.studentName}
                    </div>
                  </TableCell>

                  <TableCell>
                    <div>
                      <div className="font-medium text-gray-900">
                        {uniform.parentName}
                      </div>
                      {uniform.parentEmail && (
                        <div className="text-sm text-gray-500">
                          {uniform.parentEmail}
                        </div>
                      )}
                      {uniform.parentPhone && (
                        <div className="text-sm text-gray-500">
                          {uniform.parentPhone}
                        </div>
                      )}
                    </div>
                  </TableCell>

                  <TableCell>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                      {uniform.team}
                    </span>
                  </TableCell>

                  <TableCell>
                    <div className="space-y-1">
                      {uniform.sizes?.map((size, index) => (
                        <span key={index} className="block text-sm">
                          {size.item}: {size.size}
                        </span>
                      )) || (
                        <div>
                          {uniform.uniformTop && (
                            <div className="text-sm">
                              Top: {uniform.uniformTop}
                            </div>
                          )}
                          {uniform.uniformBottom && (
                            <div className="text-sm">
                              Bottom: {uniform.uniformBottom}
                            </div>
                          )}
                          {uniform.size && (
                            <div className="text-sm">Size: {uniform.size}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </TableCell>

                  <TableCell className="text-gray-600">
                    {formatDate(uniform.orderDate)}
                  </TableCell>

                  <TableCell>
                    {getPaymentStatusBadge(uniform.paymentStatus)}
                    {uniform.paymentAmount && (
                      <div className="text-sm text-gray-500 mt-1">
                        ${uniform.paymentAmount}
                      </div>
                    )}
                  </TableCell>

                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        uniform.received
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {uniform.received ? "Received" : "Not Received"}
                    </span>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center space-x-2">
                      {canManageUsers() && (
                        <>
                          <button
                            onClick={() => setEditingUniform(uniform)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleNotifyParent(
                                uniform.id,
                                uniform.parentEmail,
                                uniform.studentName,
                              )
                            }
                            disabled={notifyingOrderId === uniform.id}
                            className="text-green-600 hover:text-green-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {notifyingOrderId === uniform.id
                              ? "Sending..."
                              : "Notify"}
                          </button>
                          <button
                            onClick={() =>
                              handleSingleDelete(
                                uniform.id,
                                uniform.studentName,
                              )
                            }
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setViewingUniform(uniform)}
                        className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                      >
                        Details
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </Table>
        </div>
      </div>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        title="Confirm Bulk Delete"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-red-400 text-xl">⚠️</span>
              </div>
              <div className="ml-3">
                <h3 className="text-red-800 font-medium">
                  Warning: This action cannot be undone
                </h3>
                <p className="text-red-700 text-sm mt-1">
                  You are about to delete {selectedUniforms.size} uniform
                  order(s). This will permanently remove all selected orders
                  from the system.
                </p>
              </div>
            </div>
          </div>

          <p className="text-gray-600">
            Are you sure you want to proceed with the deletion?
          </p>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              onClick={() => setShowDeleteConfirmation(false)}
              variant="secondary"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkDelete}
              variant="danger"
              disabled={isDeleting}
              className="flex items-center space-x-2"
            >
              {isDeleting ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <span>🗑️</span>
                  <span>Delete {selectedUniforms.size} Orders</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Mark Received Confirmation Modal */}
      <Modal
        isOpen={showBulkReceivedConfirmation === true}
        onClose={() => setShowBulkReceivedConfirmation(null)}
        title="Confirm Mark as Received"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-green-400 text-xl">✅</span>
              </div>
              <div className="ml-3">
                <h3 className="text-green-800 font-medium">
                  Mark {selectedUniforms.size} order
                  {selectedUniforms.size > 1 ? "s" : ""} as Received
                </h3>
                <p className="text-green-700 text-sm mt-1">
                  This will mark all selected uniform orders as
                  distributed/received.
                </p>
              </div>
            </div>
          </div>

          <p className="text-gray-600">Are you sure you want to proceed?</p>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              onClick={() => setShowBulkReceivedConfirmation(null)}
              variant="secondary"
              disabled={isBulkUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleBulkReceivedUpdate(true)}
              disabled={isBulkUpdating}
              className="flex items-center space-x-2"
            >
              {isBulkUpdating ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <span>✅</span>
                  <span>Mark as Received</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Mark Not Received Confirmation Modal */}
      <Modal
        isOpen={showBulkNotReceivedConfirmation === true}
        onClose={() => setShowBulkNotReceivedConfirmation(null)}
        title="Confirm Mark as Not Received"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-yellow-400 text-xl">⏳</span>
              </div>
              <div className="ml-3">
                <h3 className="text-yellow-800 font-medium">
                  Mark {selectedUniforms.size} order
                  {selectedUniforms.size > 1 ? "s" : ""} as Not Received
                </h3>
                <p className="text-yellow-700 text-sm mt-1">
                  This will mark all selected uniform orders as not
                  received/pending distribution.
                </p>
              </div>
            </div>
          </div>

          <p className="text-gray-600">Are you sure you want to proceed?</p>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              onClick={() => setShowBulkNotReceivedConfirmation(null)}
              variant="secondary"
              disabled={isBulkUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleBulkReceivedUpdate(false)}
              disabled={isBulkUpdating}
              className="flex items-center space-x-2"
            >
              {isBulkUpdating ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <span>⏳</span>
                  <span>Mark as Not Received</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Notification Confirmation Modal */}
      <Modal
        isOpen={showNotifyConfirm}
        onClose={() => setShowNotifyConfirm(false)}
        title="Confirm Notification"
        size="md"
      >
        <div className="space-y-4">
          <p>
            Send a notification email to the parent of{" "}
            <strong>{notifyPayload.name}</strong> that their uniform is ready?
          </p>
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              onClick={() => setShowNotifyConfirm(false)}
              variant="secondary"
              disabled={notifyingOrderId !== null}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmNotify}
              disabled={notifyingOrderId !== null}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {notifyingOrderId ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      </Modal>
      {/* Edit Uniform Modal */}
      <Modal
        isOpen={!!editingUniform}
        onClose={() => setEditingUniform(null)}
        title="Edit Uniform Order"
        size="lg"
      >
        {editingUniform && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Student Name
                </label>
                <TextField
                  value={editingUniform.studentName || ""}
                  onChange={(e) =>
                    setEditingUniform({
                      ...editingUniform,
                      studentName: e.target.value,
                    })
                  }
                  placeholder="Enter student name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Name
                </label>
                <TextField
                  value={editingUniform.parentName || ""}
                  onChange={(e) =>
                    setEditingUniform({
                      ...editingUniform,
                      parentName: e.target.value,
                    })
                  }
                  placeholder="Enter parent name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Email
                </label>
                <TextField
                  type="email"
                  value={editingUniform.parentEmail || ""}
                  onChange={(e) =>
                    setEditingUniform({
                      ...editingUniform,
                      parentEmail: e.target.value,
                    })
                  }
                  placeholder="Enter parent email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Phone
                </label>
                <TextField
                  value={editingUniform.parentPhone || ""}
                  onChange={(e) =>
                    setEditingUniform({
                      ...editingUniform,
                      parentPhone: e.target.value,
                    })
                  }
                  placeholder="Enter parent phone"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team
                </label>
                <TextField
                  value={editingUniform.team || ""}
                  onChange={(e) =>
                    setEditingUniform({
                      ...editingUniform,
                      team: e.target.value,
                    })
                  }
                  placeholder="Enter team name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Status
                </label>
                <select
                  value={editingUniform.paymentStatus || "pending"}
                  onChange={(e) =>
                    setEditingUniform({
                      ...editingUniform,
                      paymentStatus: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Amount
              </label>
              <TextField
                type="number"
                step="0.01"
                value={editingUniform.paymentAmount || ""}
                onChange={(e) =>
                  setEditingUniform({
                    ...editingUniform,
                    paymentAmount: parseFloat(e.target.value) || 0,
                  })
                }
                placeholder="Enter payment amount"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Uniform Top Size
                </label>
                <TextField
                  value={editingUniform.uniformTop || ""}
                  onChange={(e) =>
                    setEditingUniform({
                      ...editingUniform,
                      uniformTop: e.target.value,
                    })
                  }
                  placeholder="Enter top size"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Uniform Bottom Size
                </label>
                <TextField
                  value={editingUniform.uniformBottom || ""}
                  onChange={(e) =>
                    setEditingUniform({
                      ...editingUniform,
                      uniformBottom: e.target.value,
                    })
                  }
                  placeholder="Enter bottom size"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="received-status"
                checked={editingUniform.received || false}
                onChange={(e) =>
                  setEditingUniform({
                    ...editingUniform,
                    received: e.target.checked,
                  })
                }
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <label
                htmlFor="received-status"
                className="ml-2 text-sm text-gray-700"
              >
                Mark as Received
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                onClick={() => setEditingUniform(null)}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditSubmit}
                className="flex items-center space-x-2"
              >
                <span>💾</span>
                <span>Save Changes</span>
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Create Manual Order Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add Manual Uniform Order"
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Student Name *
              </label>
              <TextField
                value={newUniform.studentName}
                onChange={(e) =>
                  setNewUniform({
                    ...newUniform,
                    studentName: e.target.value,
                  })
                }
                placeholder="Enter student name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent Name *
              </label>
              <TextField
                value={newUniform.parentName}
                onChange={(e) =>
                  setNewUniform({
                    ...newUniform,
                    parentName: e.target.value,
                  })
                }
                placeholder="Enter parent name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent Email
              </label>
              <TextField
                type="email"
                value={newUniform.parentEmail}
                onChange={(e) =>
                  setNewUniform({
                    ...newUniform,
                    parentEmail: e.target.value,
                  })
                }
                placeholder="Enter parent email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent Phone
              </label>
              <TextField
                value={newUniform.parentPhone}
                onChange={(e) =>
                  setNewUniform({
                    ...newUniform,
                    parentPhone: e.target.value,
                  })
                }
                placeholder="Enter parent phone"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Team *
              </label>
              <TextField
                value={newUniform.team}
                onChange={(e) =>
                  setNewUniform({
                    ...newUniform,
                    team: e.target.value,
                  })
                }
                placeholder="Enter team name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Status
              </label>
              <select
                value={newUniform.paymentStatus}
                onChange={(e) =>
                  setNewUniform({
                    ...newUniform,
                    paymentStatus: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Amount
            </label>
            <TextField
              type="number"
              step="0.01"
              value={newUniform.paymentAmount}
              onChange={(e) =>
                setNewUniform({
                  ...newUniform,
                  paymentAmount: parseFloat(e.target.value) || 0,
                })
              }
              placeholder="Enter payment amount"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Uniform Top Size
              </label>
              <TextField
                value={newUniform.uniformTop}
                onChange={(e) =>
                  setNewUniform({
                    ...newUniform,
                    uniformTop: e.target.value,
                  })
                }
                placeholder="Enter top size"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Uniform Bottom Size
              </label>
              <TextField
                value={newUniform.uniformBottom}
                onChange={(e) =>
                  setNewUniform({
                    ...newUniform,
                    uniformBottom: e.target.value,
                  })
                }
                placeholder="Enter bottom size"
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="new-received-status"
              checked={newUniform.received}
              onChange={(e) =>
                setNewUniform({
                  ...newUniform,
                  received: e.target.checked,
                })
              }
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <label
              htmlFor="new-received-status"
              className="ml-2 text-sm text-gray-700"
            >
              Mark as Received
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              onClick={() => setShowCreateModal(false)}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubmit}
              className="flex items-center space-x-2"
            >
              <span>➕</span>
              <span>Create Order</span>
            </Button>
          </div>
        </div>
      </Modal>

      {/* Uniform Details Modal */}
      <Modal
        isOpen={!!viewingUniform}
        onClose={() => setViewingUniform(null)}
        title="Uniform Order Details"
        size="lg"
      >
        {viewingUniform && (
          <div className="space-y-6">
            {" "}
            {/* Reduced overall spacing */}
            {/* Student Information */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-5 flex items-center gap-3">
                👤 Student Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Student Name</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {viewingUniform.studentName || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Team</p>
                  <span className="inline-block px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-full">
                    {viewingUniform.team || "N/A"}
                  </span>
                </div>
              </div>
            </div>
            {/* Parent Information */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-5 flex items-center gap-3">
                👨‍👩‍👧‍👦 Parent Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Parent Name</p>
                  <p className="font-medium text-gray-900">
                    {viewingUniform.parentName || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="font-medium text-gray-900 break-all">
                    {viewingUniform.parentEmail || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Phone</p>
                  <p className="font-medium text-gray-900">
                    {viewingUniform.parentPhone || "N/A"}
                  </p>
                </div>
              </div>
            </div>
            {/* Uniform Details */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-5 flex items-center gap-3">
                👕 Uniform Details
              </h3>

              <div className="space-y-3">
                {viewingUniform.sizes?.length > 0 ? (
                  viewingUniform.sizes.map((size, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center bg-gray-50 px-6 py-4 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors"
                    >
                      <span className="font-medium text-gray-700 w-32">
                        {size.item}
                      </span>
                      <span className="text-lg font-semibold text-gray-900">
                        {size.size}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {viewingUniform.uniformTop && (
                      <div className="flex justify-between items-center bg-gray-50 px-6 py-4 rounded-xl border border-gray-100">
                        <span className="font-medium text-gray-700">Top</span>
                        <span className="text-lg font-semibold text-gray-900">
                          {viewingUniform.uniformTop}
                        </span>
                      </div>
                    )}
                    {viewingUniform.uniformBottom && (
                      <div className="flex justify-between items-center bg-gray-50 px-6 py-4 rounded-xl border border-gray-100">
                        <span className="font-medium text-gray-700">
                          Bottom
                        </span>
                        <span className="text-lg font-semibold text-gray-900">
                          {viewingUniform.uniformBottom}
                        </span>
                      </div>
                    )}
                    {viewingUniform.size && (
                      <div className="flex justify-between items-center bg-gray-50 px-6 py-4 rounded-xl border border-gray-100">
                        <span className="font-medium text-gray-700">Size</span>
                        <span className="text-lg font-semibold text-gray-900">
                          {viewingUniform.size}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Order Information */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-5 flex items-center gap-3">
                📋 Order Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Order Date</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(viewingUniform.orderDate)}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Payment Status</p>
                  <div className="mt-1">
                    {getPaymentStatusBadge(viewingUniform.paymentStatus)}
                    {viewingUniform.paymentAmount && (
                      <p className="text-lg font-semibold text-emerald-600 mt-2">
                        ${viewingUniform.paymentAmount}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Received Status</p>
                  <div className="mt-1">
                    <span
                      className={`inline-flex items-center px-5 py-2 rounded-full text-sm font-medium ${
                        viewingUniform.received
                          ? "bg-green-100 text-green-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {viewingUniform.received
                        ? "✅ Received"
                        : "⏳ Not Received"}
                    </span>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Order ID</p>
                  <p className="font-mono text-sm bg-gray-100 px-4 py-2 rounded-lg w-fit text-gray-700">
                    {viewingUniform.id}
                  </p>
                </div>
              </div>
            </div>
            {/* Additional Information */}
            {(viewingUniform.notes || viewingUniform.orderSource) && (
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-5 flex items-center gap-3">
                  📝 Additional Information
                </h3>

                <div className="space-y-6">
                  {viewingUniform.orderSource && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Order Source</p>
                      <span className="inline-block px-5 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                        {viewingUniform.orderSource === "admin"
                          ? "Manual Admin Entry"
                          : "Online Purchase"}
                      </span>
                    </div>
                  )}

                  {viewingUniform.notes && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Notes</p>
                      <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5 text-gray-700 leading-relaxed">
                        {viewingUniform.notes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Action Buttons */}
            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
              <Button
                onClick={() => setViewingUniform(null)}
                variant="secondary"
                size="lg"
                className="px-8"
              >
                Close
              </Button>

              {viewingUniform.parentEmail && (
                <Button
                  onClick={() =>
                    handleNotifyParent(
                      viewingUniform.id,
                      viewingUniform.parentEmail,
                      viewingUniform.studentName,
                    )
                  }
                  disabled={notifyingOrderId === viewingUniform.id}
                  size="lg"
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 px-8"
                >
                  {notifyingOrderId === viewingUniform.id
                    ? "Sending..."
                    : "Notify Parent"}
                </Button>
              )}

              {canManageUsers() && (
                <Button
                  onClick={() => {
                    setEditingUniform(viewingUniform);
                    setViewingUniform(null);
                  }}
                  size="lg"
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 px-8"
                >
                  Edit Order
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* Print View */}
      {/* Print View */}
      {/* Print View */}
      {/* Print View */}
      {showPrintView && (
        <div
          className="fixed inset-0 z-50 bg-white overflow-auto"
          id="print-area"
          style={{ margin: 0, padding: 0 }}
        >
          <div className="p-8 max-w-6xl mx-auto" style={{ marginTop: 0 }}>
            {/* Header - only visible on screen */}
            <div className="flex justify-between items-center mb-4 no-print">
              <h1 className="text-3xl font-bold text-gray-900">
                Distribution List
              </h1>
              <div className="flex gap-3">
                <button
                  onClick={() => window.print()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Print
                </button>
                <button
                  onClick={() => setShowPrintView(false)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Print Header Info */}
            <div className="mb-6 text-sm text-gray-600 border-b border-gray-300 pb-4 print:mb-5">
              <p className="font-medium">
                Printed on{" "}
                {new Date().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}{" "}
                • {filteredUniforms.length} uniforms
              </p>
              {filters.team && (
                <p className="mt-1">
                  Team: <span className="font-semibold">{filters.team}</span>
                </p>
              )}
              {filters.status && (
                <p>
                  Status:{" "}
                  <span className="font-semibold">
                    {filters.status === "not_received"
                      ? "Not Received"
                      : "Received"}
                  </span>
                </p>
              )}
            </div>

            {/* Distribution Table */}
            <table className="w-full border-collapse text-sm print:text-xs">
              <thead>
                <tr className="border-b-2 border-gray-800 bg-gray-100">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Student
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Team
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Parent
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Phone
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Top Size
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Bottom Size
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Payment
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 w-20">
                    Distributed
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUniforms.map((u) => (
                  <tr key={u.id} className="border-b border-gray-200">
                    <td className="py-3 px-4 font-medium">{u.studentName}</td>
                    <td className="py-3 px-4">{u.team}</td>
                    <td className="py-3 px-4">{u.parentName}</td>
                    <td className="py-3 px-4 font-mono text-xs">
                      {u.parentPhone || "-"}
                    </td>
                    <td className="py-3 px-4">{u.uniformTop || "-"}</td>
                    <td className="py-3 px-4">{u.uniformBottom || "-"}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${u.received ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}
                      >
                        {u.received ? "Received" : "Not Received"}
                      </span>
                    </td>
                    <td className="py-3 px-4 capitalize">
                      {u.paymentStatus || "-"}
                    </td>
                    <td className="py-3 px-4 w-20">
                      <input
                        type="checkbox"
                        checked={printChecked.has(u.id)}
                        onChange={(e) => {
                          const newSet = new Set(printChecked);
                          if (e.target.checked) newSet.add(u.id);
                          else newSet.delete(u.id);
                          setPrintChecked(newSet);
                        }}
                        className="w-4 h-4 rounded border-gray-400"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <style jsx global>{`
            @media print {
              body,
              #print-area,
              #print-area > div {
                margin: 0 !important;
                padding-top: 0 !important;
                padding-left: 0 !important;
                padding-right: 0 !important;
              }
              .no-print {
                display: none !important;
              }
              #print-area {
                position: static !important;
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}

export default UniformManagement;